/**
 * Lot 35 — Admin Users (global admin only).
 *
 * Routes:
 *   /admin/users           — list with filters and pagination
 *   /admin/users/:id       — full detail (memberships + stats)
 *
 * ACL: redirect to / for non-admins; backend already returns 403.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Search, RefreshCcw, ChevronLeft, ChevronRight,
  Eye, PauseCircle, PlayCircle, Mail, Building2, GraduationCap,
  CheckCircle2, AlertTriangle, XCircle,
} from 'lucide-react';
import { GlassCard, SectionHeader, PageLoading } from '../components/shared';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { adminUsersApi, madrassaApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

// ---------------------------------------------------------------------------
// Helpers / shared little UI bits
// ---------------------------------------------------------------------------
const ROLE_META = {
  admin:   { fr: 'Admin', cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
  user:    { fr: 'Utilisateur', cls: 'bg-surface-elevated text-text-secondary border-border' },
};
const STATUS_META = {
  active:    { fr: 'Actif', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  suspended: { fr: 'Suspendu', cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
};
const SUPPORT_META = {
  on_track: { fr: 'En forme', icon: CheckCircle2, cls: 'text-emerald-300' },
  needs_reinforcement: { fr: 'À renforcer', icon: AlertTriangle, cls: 'text-amber-300' },
  needs_help: { fr: 'À aider', icon: XCircle, cls: 'text-rose-300' },
};

export const RoleBadge = ({ role }) => {
  const meta = ROLE_META[role] || ROLE_META.user;
  return <Badge className={`${meta.cls} border text-[10px]`} data-role={role}>{meta.fr}</Badge>;
};
export const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.active;
  return <Badge className={`${meta.cls} border text-[10px]`} data-status={status}>{meta.fr}</Badge>;
};
export const SupportBadge = ({ support }) => {
  if (!support) return null;
  const meta = SUPPORT_META[support];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] ${meta.cls}`}
          data-testid={`support-${support}`}>
      <Icon className="w-3 h-3" /> {meta.fr}
    </span>
  );
};

const fmtDate = (s) => (s ? String(s).slice(0, 10) : '—');

// ---------------------------------------------------------------------------
// LIST PAGE
// ---------------------------------------------------------------------------
export const AdminUsersPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('all');
  const [status, setStatus] = useState('all');
  const [madrassa, setMadrassa] = useState('all');
  const [sort, setSort] = useState('created_desc');
  const [page, setPage] = useState(1);
  const [madrassas, setMadrassas] = useState([]);
  const limit = 25;

  const isAdmin = isAuthenticated && user?.role === 'admin';

  const refresh = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const params = { page, limit, sort };
      if (q.trim()) params.q = q.trim();
      if (role !== 'all') params.role = role;
      if (status !== 'all') params.status = status;
      if (madrassa !== 'all') params.madrassaId = madrassa;
      const r = await adminUsersApi.list(params);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) { navigate('/', { replace: true }); return; }
    // Load madrassas list once for the filter dropdown
    madrassaApi.adminList({ with_stats: false })
      .then((r) => setMadrassas(r.data?.items || []))
      .catch(() => setMadrassas([]));
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAdmin]);

  useEffect(() => { if (isAdmin) refresh(); /* eslint-disable-next-line */ }, [page, sort]);

  const onSearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    refresh();
  };

  const onChangeStatus = async (u, newStatus) => {
    try {
      await adminUsersApi.setStatus(u.id, newStatus);
      refresh();
    } catch { /* ignore */ }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (authLoading) return <PageLoading />;
  if (!isAdmin) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
         data-testid="admin-users-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/')}
              className="mb-3" data-testid="admin-users-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour
      </Button>

      <SectionHeader
        overline="Console admin global"
        title="Utilisateurs"
        subtitle="Vue consolidée de tous les comptes : rôles, rattachements madrassa, statut et activité."
      />

      {/* Toolbar */}
      <form onSubmit={onSearch}
            className="mt-6 flex flex-wrap items-center gap-2"
            data-testid="admin-users-toolbar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input
            placeholder="Rechercher par email ou nom…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8"
            data-testid="admin-users-search"
          />
        </div>
        <Select value={role} onValueChange={(v) => { setRole(v); setPage(1); }}>
          <SelectTrigger className="w-[140px]" data-testid="admin-users-filter-role">
            <SelectValue placeholder="Rôle" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous rôles</SelectItem>
            <SelectItem value="user">Utilisateur</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-[140px]" data-testid="admin-users-filter-status">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="active">Actifs</SelectItem>
            <SelectItem value="suspended">Suspendus</SelectItem>
          </SelectContent>
        </Select>
        <Select value={madrassa} onValueChange={(v) => { setMadrassa(v); setPage(1); }}>
          <SelectTrigger className="w-[200px]" data-testid="admin-users-filter-madrassa">
            <SelectValue placeholder="Madrassa" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes madrassas</SelectItem>
            {madrassas.map((m) => (
              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => { setSort(v); setPage(1); }}>
          <SelectTrigger className="w-[170px]" data-testid="admin-users-sort">
            <SelectValue placeholder="Tri" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="created_desc">Plus récents</SelectItem>
            <SelectItem value="created_asc">Plus anciens</SelectItem>
            <SelectItem value="email_asc">Email (A→Z)</SelectItem>
            <SelectItem value="role_asc">Rôle</SelectItem>
            <SelectItem value="status_asc">Statut</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" size="sm" data-testid="admin-users-search-submit">
          <Search className="w-3.5 h-3.5 mr-1" /> Rechercher
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={refresh}
                data-testid="admin-users-refresh">
          <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Rafraîchir
        </Button>
      </form>

      {/* List */}
      <div className="mt-6 space-y-2">
        {loading && (
          <p className="text-sm text-text-muted" data-testid="admin-users-loading">
            Chargement…
          </p>
        )}
        {!loading && items.length === 0 && (
          <GlassCard data-testid="admin-users-empty">
            <p className="text-sm text-text-muted text-center py-4">
              Aucun utilisateur trouvé.
            </p>
          </GlassCard>
        )}
        {!loading && items.map((u) => (
          <GlassCard key={u.id} data-testid={`admin-user-row-${u.id}`}>
            <div className="flex items-start gap-3 flex-wrap">
              <div className="w-9 h-9 rounded-md bg-primary/15 text-primary flex items-center justify-center shrink-0">
                <Mail className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/admin/users/${u.id}`}
                        className="text-sm font-medium text-text-primary hover:text-primary truncate"
                        data-testid={`admin-user-row-${u.id}-name`}>
                    {u.name || u.email}
                  </Link>
                  <RoleBadge role={u.role} />
                  <StatusBadge status={u.status || 'active'} />
                </div>
                <div className="flex items-center gap-2 flex-wrap mt-1 text-[11px] text-text-muted">
                  <span>{u.email}</span>
                  <span>·</span>
                  <span>Créé : {fmtDate(u.createdAt)}</span>
                </div>
                {u.madrassaMemberships?.length > 0 && (
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[11px] text-text-secondary">
                    <Building2 className="w-3 h-3 text-primary" />
                    {u.madrassaMemberships.slice(0, 3).map((mm, i) => (
                      <Badge key={i}
                             className="bg-primary/10 text-primary border border-primary/20 text-[10px]">
                        {mm.madrassaName} · {mm.role}
                      </Badge>
                    ))}
                    {u.madrassaMemberships.length > 3 && (
                      <span>+{u.madrassaMemberships.length - 3}</span>
                    )}
                  </div>
                )}
                {u.classMemberships?.length > 0 && (
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap text-[11px] text-text-secondary">
                    <GraduationCap className="w-3 h-3 text-primary" />
                    {u.classMemberships.slice(0, 4).map((cm, i) => (
                      <Badge key={i} className="bg-surface-elevated border border-border text-text-secondary text-[10px]">
                        {cm.className} ({cm.role})
                      </Badge>
                    ))}
                    {u.classMemberships.length > 4 && (
                      <span>+{u.classMemberships.length - 4}</span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <Button size="sm" variant="ghost"
                        onClick={() => navigate(`/admin/users/${u.id}`)}
                        data-testid={`admin-user-row-${u.id}-detail`}
                        title="Détail">
                  <Eye className="w-4 h-4" />
                </Button>
                {(u.status || 'active') === 'active' ? (
                  <Button size="sm" variant="ghost"
                          onClick={() => onChangeStatus(u, 'suspended')}
                          data-testid={`admin-user-row-${u.id}-suspend`}
                          title="Suspendre">
                    <PauseCircle className="w-4 h-4 text-amber-300" />
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost"
                          onClick={() => onChangeStatus(u, 'active')}
                          data-testid={`admin-user-row-${u.id}-reactivate`}
                          title="Réactiver">
                    <PlayCircle className="w-4 h-4 text-emerald-300" />
                  </Button>
                )}
              </div>
            </div>
          </GlassCard>
        ))}
      </div>

      {/* Pagination */}
      {!loading && total > limit && (
        <div className="mt-4 flex items-center justify-between"
             data-testid="admin-users-pagination">
          <p className="text-xs text-text-muted">
            {total} utilisateurs · page {page} / {totalPages}
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                    data-testid="admin-users-prev">
              <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Préc.
            </Button>
            <Button size="sm" variant="ghost"
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                    data-testid="admin-users-next">
              Suiv. <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// DETAIL PAGE
// ---------------------------------------------------------------------------
export const AdminUserDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAdmin = isAuthenticated && user?.role === 'admin';

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await adminUsersApi.detail(id);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Utilisateur introuvable.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) { navigate('/', { replace: true }); return; }
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAdmin, id]);

  const onChangeStatus = async (newStatus) => {
    try {
      await adminUsersApi.setStatus(id, newStatus);
      refresh();
    } catch { /* ignore */ }
  };

  const isTeacher = useMemo(
    () => (data?.classMemberships || []).some((c) => c.role === 'teacher'),
    [data],
  );

  if (authLoading || loading) return <PageLoading />;
  if (!isAdmin) return null;
  if (error || !data) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
           data-testid="admin-user-detail-error">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/users')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour à la liste
        </Button>
        <p className="text-sm text-rose-300 mt-4">{error || 'Utilisateur introuvable.'}</p>
      </div>
    );
  }

  const a = data.assignmentStats || {};
  const p = data.progressSummary || {};

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
         data-testid="admin-user-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/admin/users')}
              className="mb-3" data-testid="admin-user-detail-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour à la liste
      </Button>

      <GlassCard data-testid="admin-user-detail-header">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <Mail className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-serif text-text-primary truncate"
                  data-testid="admin-user-detail-name">
                {data.name || data.email}
              </h2>
              <RoleBadge role={data.role} />
              <StatusBadge status={data.status || 'active'} />
            </div>
            <p className="text-sm text-text-secondary mt-1">{data.email}</p>
            <div className="flex items-center gap-2 mt-2 flex-wrap text-[11px] text-text-muted">
              <span>id : <code>{data.id}</code></span>
              <span>· créé : {fmtDate(data.createdAt)}</span>
              {data.lastActivityAt && (
                <span>· dernière activité : {fmtDate(data.lastActivityAt)}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {(data.status || 'active') === 'active' ? (
              <Button size="sm" variant="outline"
                      onClick={() => onChangeStatus('suspended')}
                      data-testid="admin-user-detail-suspend">
                <PauseCircle className="w-3.5 h-3.5 mr-1 text-amber-300" /> Suspendre
              </Button>
            ) : (
              <Button size="sm" variant="outline"
                      onClick={() => onChangeStatus('active')}
                      data-testid="admin-user-detail-reactivate">
                <PlayCircle className="w-3.5 h-3.5 mr-1 text-emerald-300" /> Réactiver
              </Button>
            )}
          </div>
        </div>
      </GlassCard>

      {/* Madrassa memberships */}
      {data.madrassaMemberships?.length > 0 && (
        <GlassCard className="mt-4" data-testid="admin-user-detail-madrassas">
          <h3 className="text-sm font-medium text-text-primary mb-2">
            <Building2 className="w-4 h-4 inline mr-1.5 text-primary" />
            Madrassas ({data.madrassaMemberships.length})
          </h3>
          <div className="space-y-2">
            {data.madrassaMemberships.map((m, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <Link to={`/admin/madrassas/${m.madrassaId}`}
                      className="text-text-primary hover:text-primary">
                  {m.madrassaName}
                </Link>
                <Badge className="bg-primary/10 text-primary border border-primary/20 text-[10px]">
                  {m.role}
                </Badge>
                <span className="text-[11px] text-text-muted">
                  {m.membershipStatus} · {fmtDate(m.joinedAt)}
                </span>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Class memberships */}
      {data.classMemberships?.length > 0 && (
        <GlassCard className="mt-4" data-testid="admin-user-detail-classes">
          <h3 className="text-sm font-medium text-text-primary mb-2">
            <GraduationCap className="w-4 h-4 inline mr-1.5 text-primary" />
            Classes ({data.classMemberships.length})
          </h3>
          <div className="space-y-2">
            {data.classMemberships.map((c, i) => (
              <div key={i} className="flex items-center gap-3 text-sm flex-wrap">
                <span className="text-text-primary">{c.className}</span>
                <Badge className="bg-surface-elevated border border-border text-text-secondary text-[10px]">
                  {c.role}
                </Badge>
                <span className="text-[11px] text-text-muted">{c.madrassaName}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Stats grid */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Assignment */}
        <GlassCard data-testid="admin-user-detail-assignments">
          <h3 className="text-sm font-medium text-text-primary mb-2">Devoirs</h3>
          <ul className="text-sm space-y-1">
            <li>Reçus : <strong>{a.assigned ?? 0}</strong></li>
            <li>Terminés : <strong>{a.completed ?? 0}</strong></li>
            <li>Score moyen : <strong>{a.averageScore != null ? `${a.averageScore}%` : 'Non disponible'}</strong></li>
            <li className="text-[11px] text-text-muted">
              Dernière soumission : {fmtDate(a.lastSubmittedAt)}
            </li>
          </ul>
        </GlassCard>

        {/* Progress */}
        <GlassCard data-testid="admin-user-detail-progress">
          <h3 className="text-sm font-medium text-text-primary mb-2">Progression</h3>
          <ul className="text-sm space-y-1">
            <li>Lecture : <strong>
              {p.readingProgressPercent != null ? `${p.readingProgressPercent}%` : 'Non disponible'}
            </strong></li>
            <li>Tajwīd : <strong>
              {p.tajweedProgressPercent != null ? `${p.tajweedProgressPercent}%` : 'Non disponible'}
            </strong></li>
            <li>Compétences maîtrisées : <strong>
              {p.masteredCompetencies ?? 0}/{p.totalCompetencies ?? 0}
            </strong></li>
            {(p.weakSkills?.length > 0 || p.weakTajweedRules?.length > 0) && (
              <li className="mt-2 pt-2 border-t border-border/40">
                <span className="text-[11px] text-text-muted">Points faibles :</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(p.weakSkills || []).map((s, i) => (
                    <Badge key={`r-${i}`}
                           className="bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px]">
                      {s}
                    </Badge>
                  ))}
                  {(p.weakTajweedRules || []).map((r, i) => (
                    <Badge key={`t-${i}`}
                           className="bg-rose-500/15 text-rose-300 border border-rose-500/30 text-[10px]">
                      {r}
                    </Badge>
                  ))}
                </div>
              </li>
            )}
          </ul>
        </GlassCard>

        {/* Teacher-specific */}
        {isTeacher && data.teacherStats && (
          <GlassCard data-testid="admin-user-detail-teacher-stats" className="md:col-span-2">
            <h3 className="text-sm font-medium text-text-primary mb-2">
              Activité d&apos;enseignant
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><span className="text-text-muted text-[11px] block">Classes</span>
                   <strong>{data.teacherStats.classesCount}</strong></div>
              <div><span className="text-text-muted text-[11px] block">Élèves suivis</span>
                   <strong>{data.teacherStats.studentsCount}</strong></div>
              <div><span className="text-text-muted text-[11px] block">Devoirs envoyés</span>
                   <strong>{data.teacherStats.assignmentsSent}</strong></div>
              <div><span className="text-text-muted text-[11px] block">Score moyen reçu</span>
                   <strong>{data.teacherStats.averageSubmissionScore != null
                     ? `${data.teacherStats.averageSubmissionScore}%` : '—'}</strong></div>
            </div>
          </GlassCard>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;
